import React, { useMemo, useState } from 'react';
import {
  Col, Row, Spinner,
} from 'react-bootstrap';
import { useSelector } from 'react-redux';

import FilterSelector from 'components/Flux/Automatic/FilterSelector';
import MLTSelector from 'components/Flux/Automatic/MLTSelector';
import ActiveFilters from 'components/Flux/Automatic/ActiveFilters';
import RevisionSelector from 'components/Flux/Automatic/RevisionSelector';
import RevisionSubTableModal from 'components/Flux/Revision/SubTableModal';
import RevisionTable from 'components/Flux/Revision/Table';
import Snackbar from 'components/DesignSystem/Snackbar';
import usePastSimulationNames from 'utils/hooks/flux/automatic/usePastSimulationNames';
import useFluxAutomaticZipDownload from 'utils/hooks/flux/automatic/useFluxAutomaticZipDownload';
import useRevisionData from 'utils/hooks/flux/automatic/useRevisionData';
import useRevisionScenarios from 'utils/hooks/flux/automatic/useRevisionScenarios';
import usePeriodControls from 'hooks/Flux/Automatic/Revision/usePeriodControls';
import { buildSubdivisionParam } from 'helper/flux/automatic';
import {
  selectFluxAutomaticControlPanelMltOption,
  selectFluxAutomaticControlPanelSelectedRevision,
  selectFluxAutomaticControlPanelSpatialSubdivision,
  selectFluxAutomaticControlPanelAutomaticStudyScenarios,
} from 'redux/reducers/fluxAutomaticControlPanelReducer';
import { cssVar } from 'constants/theme';

export default function RevisionDesktop() {
  const { pastSimulationNames } = usePastSimulationNames();
  const unidade = useSelector(selectFluxAutomaticControlPanelMltOption);
  const models = useSelector(selectFluxAutomaticControlPanelAutomaticStudyScenarios);
  const review = useSelector(selectFluxAutomaticControlPanelSelectedRevision);
  const subdivision = useSelector(selectFluxAutomaticControlPanelSpatialSubdivision);
  const [isOpen, setIsOpen] = useState(false);
  const [modelName, setModelName] = useState('');
  const [period, setPeriod] = useState('');
  const { expectedDataResponse, revisionRows, isFetching } = useRevisionData();
  const {
    data,
    isFetching: isFetchingScenarios,
    errorMessage,
  } = useRevisionScenarios(modelName, period);
  const dataIsEmpty = !revisionRows.some((r) => r.values.some((v) => v.value !== '—'));
  const periodList = expectedDataResponse?.colLabels ?? [];
  const {
    toFirst,
    toPrevious,
    toNext,
    toLast,
    isFirstPeriod,
    isLastPeriod,
  } = usePeriodControls(period, periodList, setPeriod);

  const popDataModal = (chosenModel: string, chosenPeriod: string) => {
    setIsOpen(true);
    setModelName(chosenModel);
    setPeriod(chosenPeriod);
  };
  const spatialSubdivisionParam = useMemo(() => buildSubdivisionParam(subdivision), [subdivision]);
  const { DownloadZipButton, DownloadConfirmationDialog } = useFluxAutomaticZipDownload({
    models,
    review,
    unidade,
    type: 'revision',
    pastSimulationNames,
    spatialSubdivisionParam,
  });

  return (
    <>
      <RevisionSubTableModal
        isOpen={isOpen}
        period={period}
        data={data?.data}
        revision={review}
        modelName={modelName}
        errorMessage={errorMessage}
        onClose={() => setIsOpen(false)}
        toFirst={toFirst}
        toPrevious={toPrevious}
        toNext={toNext}
        toLast={toLast}
        isFirstPeriod={isFirstPeriod}
        isLastPeriod={isLastPeriod}
        isFetchingScenarios={isFetchingScenarios}
      />
      <div className="pt-4 px-5">
        <DownloadConfirmationDialog />
        <Row className="px-3 pb-2" style={{ backgroundColor: cssVar('bg-subtle') }}>
          <Col xs={3}>
            <FilterSelector />
          </Col>
          <Col>
            <MLTSelector disabled />
          </Col>
          <Col>
            <RevisionSelector />
          </Col>
          <Col xs={1}>
            <DownloadZipButton />
          </Col>
        </Row>
        <Row className="py-2" style={{ backgroundColor: cssVar('bg-subtle') }}>
          <Col className="ps-0"><ActiveFilters /></Col>
        </Row>
        <div className="overflow-auto" style={{ height: 'calc(90vh - 120px)' }}>
          { isFetching && <div className="text-center py-4"><Spinner /></div> }
          { !isFetching && dataIsEmpty && (
            <Snackbar>
              Nenhum dado para os filtros selecionados.
            </Snackbar>
          )}
          { !isFetching && !dataIsEmpty && <RevisionTable popDataModal={popDataModal} /> }
        </div>
      </div>
    </>
  );
}
